import FormInput from "../../molecules/FormInput";
import { FormEvent, useEffect, useState } from "react";
import { Stack } from "@mui/system";
import {
  Department,
  Ticket,
  TicketPriorityEnum,
  TicketStatusEnum,
} from "../../../services/swagger";
import {
  DepartmentService,
  SettingsService,
  TicketService,
} from "../../../services/api";
import useConfirm from "../../../hooks/useConfirm";
import useRefresh from "../../../hooks/useRefresh";
import Authorized from "../../atoms/Authorized";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import useEnums from "../../../hooks/useEnums";
import useRole from "../../../hooks/useRole";
import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  Divider,
  Typography,
} from "@mui/material";
import AvatarWithInitials from "../../atoms/AvatarWithInitials";
import { DropDownItem } from "../../molecules/Dropdown";
import { toast } from "react-toastify";
import FieldLabel from "../../atoms/FieldLabel";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import BusinessIcon from "@mui/icons-material/Business";
import { useBusinessStore } from "../../../stores/Dummy";

interface TicketInfoProps {
  ticket: Ticket;
}

export default function TicketInfo(props: TicketInfoProps) {
  const { t } = useTranslation("Ticket");
  const role = useRole();
  const { statusDropDownItems, priorityDropDownItems } = useEnums();
  const { ticket } = props;
  const [department, setDepartment] = useState<string>(
    ticket.department as unknown as string,
  );
  const [priority, setPriority] = useState<TicketPriorityEnum>(ticket.priority);
  const [status, setStatus] = useState<TicketStatusEnum>(ticket.status);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [company, setCompany] = useState<string>(
    (ticket.user?.company || ticket.company?._id) as unknown as string,
  );
  const [cities, setCities] = useState<DropDownItem[]>([]);
  const [city, setCity] = useState(ticket.city);
  const [extRef, setExtRef] = useState(ticket.extRef);
  const [location, setLocation] = useState(ticket.location);
  const [person, setPerson] = useState(ticket.person);
  const [number, setNumber] = useState(ticket.number);
  const [infoOpen, setInfoOpen] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);

  const miniCompanyList = useBusinessStore((state) => state.miniCompanyList);

  const confirm = useConfirm();
  const refresh = useRefresh();

  const loadDepartments = async () => {
    const res = await DepartmentService.getDepartmentMiniList();
    if (!res?.data?.data) return;
    setDepartments(res.data.data);
    if (res.data.data.length > 0)
      setDepartment((ticket.department as unknown as Department)?._id || "");
  };
  const loadCities = async () => {
    const res = await SettingsService.getCities();
    if (!res?.data?.data) return;
    setCities(res.data.data.map((e) => ({ name: e, value: e })));
    if (res.data.data.length > 0 && !city) setCity(res.data.data[0]);
  };

  useEffect(() => {
    loadDepartments();
    loadCities();
  }, []);

  useEffect(() => {
    setStatus(ticket.status);
  }, [ticket.status]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    confirm(async () => {
      if (!ticket?._id) return;
      if (!ticket.department?._id)
        return toast(t("MustSelectDepartment"), { type: "error" });
      const res = await TicketService.updateTicket({
        id: ticket._id,
        updateTicketBody: {
          city,
          location,
          priority,
          status,
          title: ticket.title,
          person,
          number,
          company,
          departmentId: department,
          extRef,
          assignees: ticket.assignees
            .filter((u) => u._id?.toString())
            .map((user) => user._id?.toString() || ""),
        },
      });
      if (res?.data?.success) refresh();
    });
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: (theme) => (theme.shape.borderRadius as number) / 8,
      }}
      component="form"
      onSubmit={handleSubmit}
    >
      <Stack spacing={2}>
        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          <AvatarWithInitials size={40}>
            {ticket.user?.name || ticket.person}
          </AvatarWithInitials>
          <Stack>
            <Typography fontSize="medium" fontWeight={600}>
              {ticket.user?.name || ticket.person}
            </Typography>
            <Typography color="text.secondary">
              {
                (ticket.user?.company ||
                  ticket.companyName) as unknown as string
              }
            </Typography>
          </Stack>
        </Stack>
        <Divider />
        <ButtonBase onClick={() => setInfoOpen(!infoOpen)} disableRipple>
          <Stack direction={"row"} spacing={"auto"} width={1}>
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
              <InfoIcon color="primary" fontSize="small" />
              <FieldLabel>{t("DetailsPlaceholder")}</FieldLabel>
            </Stack>
            {infoOpen ? (
              <KeyboardArrowUpIcon sx={{ color: "text.secondary" }} />
            ) : (
              <KeyboardArrowDownIcon sx={{ color: "text.secondary" }} />
            )}
          </Stack>
        </ButtonBase>

        <Collapse in={infoOpen}>
          <Stack spacing={2} flexGrow={1}>
            <FormInput
              role="agent"
              field="status"
              label={t("Status")}
              onChange={(v) => setStatus(v as TicketStatusEnum)}
              type="list"
              value={status}
              list={statusDropDownItems}
              required
            />
            <FormInput
              role="agent"
              field="priority"
              label={t("Priority")}
              onChange={(v) => setPriority(v as TicketPriorityEnum)}
              type="list"
              value={priority}
              list={priorityDropDownItems}
              required
            />
            {(department || role("agent")) && (
              <FormInput
                role="agent"
                field="departmentId"
                label={t("Department")}
                type="list"
                placeholder={t("DepartmentPlaceholder")}
                list={departments.map((department) => {
                  return {
                    name: department.name,
                    value: department._id || "",
                  };
                })}
                onChange={(v) => setDepartment(v as string)}
                value={department}
                required
              />
            )}
          </Stack>
        </Collapse>
        <Divider />
        <ButtonBase onClick={() => setContactOpen(!contactOpen)} disableRipple>
          <Stack direction={"row"} spacing={"auto"} width={1}>
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
              <BusinessIcon color="primary" fontSize="small" />
              <FieldLabel>{t("ContactInfo")}</FieldLabel>
            </Stack>
            {contactOpen ? (
              <KeyboardArrowUpIcon sx={{ color: "text.secondary" }} />
            ) : (
              <KeyboardArrowDownIcon sx={{ color: "text.secondary" }} />
            )}
          </Stack>
        </ButtonBase>

        <Collapse in={contactOpen}>
          <Stack spacing={2} flexGrow={1}>
            {role("agent") && (
              <FormInput
                role="agent"
                field="company"
                label={t("Company")}
                placeholder={t("CompanyPlaceholder")}
                onChange={(v) => {
                  setCompany(v as string);
                }}
                onSoloTextChange={(v) => console.log(v)}
                type="list"
                list={miniCompanyList.map?.((company) => ({
                  name: `${company.name} (${company.regNo})`,
                  value: company._id,
                }))}
                value={company}
                required={!!company}
              />
            )}
            {(person || role("agent")) && (
              <FormInput
                role="agent"
                field="person"
                label={t("Person")}
                type="string"
                placeholder={t("Person")}
                onChange={(v) => setPerson(v as string)}
                value={person}
                required
              />
            )}
            {(number || role("agent")) && (
              <FormInput
                role="agent"
                field="number"
                label={t("Phone")}
                type="string"
                placeholder={t("PhonePlaceholder")}
                onChange={(v) => setNumber(v as string)}
                value={number}
              />
            )}
            {(city || role("agent")) && (
              <FormInput
                role="agent"
                field="city"
                label={t("City")}
                type="list"
                placeholder={t("CityPlaceholder")}
                onChange={(v) => setCity(v as string)}
                value={city}
                list={cities}
                required
              />
            )}
            {(location || role("agent")) && (
              <FormInput
                role="agent"
                field="location"
                label={t("Location")}
                type="string"
                placeholder={t("LocationPlaceholder")}
                onChange={(v) => setLocation(v as string)}
                value={location}
              />
            )}
            {role("agent") && (
              <FormInput
                role="agent"
                field="extRef"
                label={t("ExtRef")}
                type="string"
                placeholder={t("ExtRefPlaceholder")}
                onChange={(v) => setExtRef(v as string)}
                value={extRef}
              />
            )}
          </Stack>
        </Collapse>
        <Authorized role="agent">
          <Button
            variant="contained"
            color="success"
            type="submit"
            fullWidth
            sx={{ flexGrow: 1 }}
          >
            {t("Update")}
          </Button>
        </Authorized>
      </Stack>
    </Box>
  );
}
